import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Users, Building2, Mail, Phone, MapPin, CreditCard, AlertCircle, Save, User } from 'lucide-react';

const BACKEND_URL = '';
const employeeApi = axios.create({ baseURL: BACKEND_URL, timeout: 30000 });
employeeApi.interceptors.request.use((config) => { const token = localStorage.getItem('employee_token'); if (token) config.headers.Authorization = `Bearer ${token}`; return config; });

export default function EmployeeOrganisationPage() {
  const [activeTab, setActiveTab] = useState('employees');
  const [myProfile, setMyProfile] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchMyProfile();
    fetchDepartments();
    fetchAllEmployees();
  }, []);

  const fetchMyProfile = async () => {
    try {
      const r = await employeeApi.get('/api/employee/me');
      setMyProfile(r.data);
      setEditForm({
        mobile: r.data?.personal?.mobile || '',
        address: r.data?.personal?.address || '',
        emergency_contact_name: r.data?.emergency?.name || '',
        emergency_contact_number: r.data?.emergency?.phone || ''
      });
    } catch (e) {
      console.error('Error fetching profile:', e);
    }
  };

  const fetchDepartments = async () => {
    try {
      const r = await employeeApi.get('/api/departments');
      setDepartments(r.data || []);
    } catch (e) {
      console.error('Error fetching departments:', e);
    }
  };

  const fetchAllEmployees = async () => {
    try {
      const r = await employeeApi.get('/api/employee/team');
      setAllEmployees(r.data || []);
    } catch (e) {
      console.error('Error fetching employees:', e);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await employeeApi.put('/api/employee/me', {
        personal: {
          ...myProfile?.personal,
          mobile: editForm.mobile,
          address: editForm.address
        },
        emergency: {
          name: editForm.emergency_contact_name,
          phone: editForm.emergency_contact_number
        }
      });
      toast.success('Profile updated successfully');
      fetchMyProfile();
      setIsEditing(false);
    } catch (e) {
      toast.error('Failed to update profile');
    }
  };

  const getDepartmentNames = (deptNames) => {
    // Departments are now stored as names, not ObjectIds
    if (!deptNames || deptNames.length === 0) return 'N/A';
    return deptNames.join(', ');
  };

  const getDepartmentEmployees = (dept) => {
    // Match by department name since employees store department names, not IDs
    return allEmployees.filter(emp => 
      emp.departments?.includes(dept.name) || 
      emp.employment?.departments?.includes(dept.name)
    );
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#1a1a2e]">Organisation</h1>
        <p className="text-neutral-600 mt-1">View your details and organization structure</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6 border-b border-white/40">
        <button onClick={() => setActiveTab('employees')} className={`px-6 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'employees' ? 'border-yellow-600 text-yellow-600' : 'border-transparent text-neutral-600 hover:text-[#1a1a2e]'}`}>
          <Users className="w-4 h-4" /> My Profile
        </button>
        <button onClick={() => setActiveTab('departments')} className={`px-6 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'departments' ? 'border-yellow-600 text-yellow-600' : 'border-transparent text-neutral-600 hover:text-[#1a1a2e]'}`}>
          <Building2 className="w-4 h-4" /> Departments
        </button>
      </div>

      {/* My Profile Tab */}
      {activeTab === 'employees' && myProfile && (
        <div className="space-y-6">
          {/* Profile Header */}
          <div className="glass-card p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center">
                  <User className="w-10 h-10 text-yellow-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#1a1a2e]">{myProfile.personal?.full_name || 'N/A'}</h2>
                  <p className="text-neutral-600">{myProfile.designation || 'N/A'}</p>
                  <p className="text-sm text-neutral-500 mt-1">Employee ID: {myProfile.employee_id || 'N/A'}</p>
                </div>
              </div>
              {!isEditing ? (
                <button onClick={() => setIsEditing(true)} className="px-4 py-2 border border-white/40 rounded-xl text-neutral-700 hover:bg-white/60 font-medium">
                  Edit Profile
                </button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setIsEditing(false)} className="px-4 py-2 border border-white/40 rounded-xl text-neutral-700 hover:bg-white/60 font-medium">
                    Cancel
                  </button>
                  <button onClick={handleSaveProfile} className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-xl hover:opacity-90 font-medium flex items-center gap-2">
                    <Save className="w-4 h-4" /> Save
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Personal Information (Read Only) */}
            <div className="glass-card p-6">
              <h3 className="font-bold text-lg text-[#1a1a2e] mb-4">Personal Information</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 mb-1">First Name</label>
                    <p className="text-[#1a1a2e]">
                      {myProfile.personal?.first_name || 
                       (myProfile.personal?.full_name ? myProfile.personal.full_name.split(' ')[0] : 'Not provided')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 mb-1">Last Name</label>
                    <p className="text-[#1a1a2e]">
                      {myProfile.personal?.last_name || 
                       (myProfile.personal?.full_name ? myProfile.personal.full_name.split(' ').slice(1).join(' ') || 'Not provided' : 'Not provided')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Work Information (Read Only) */}
            <div className="glass-card p-6">
              <h3 className="font-bold text-lg text-[#1a1a2e] mb-4">Work Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Email</label>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-neutral-400" />
                    <p className="text-[#1a1a2e]">{myProfile.work_email}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Department(s)</label>
                  <p className="text-[#1a1a2e]">{getDepartmentNames(myProfile.departments)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Designation</label>
                  <p className="text-[#1a1a2e]">{myProfile.designation || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Contact Information (Editable) */}
            <div className="glass-card p-6">
              <h3 className="font-bold text-lg text-[#1a1a2e] mb-4">Contact Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Mobile Number</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editForm.mobile}
                      onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                      className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500"
                      placeholder="Enter mobile number"
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-neutral-400" />
                      <p className="text-[#1a1a2e]">{myProfile.personal?.mobile || 'Not provided'}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Address</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.address}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500"
                      placeholder="Enter address"
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-neutral-400" />
                      <p className="text-[#1a1a2e]">{myProfile.personal?.address || 'Not provided'}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Identity Documents (Read Only) */}
            <div className="glass-card p-6">
              <h3 className="font-bold text-lg text-[#1a1a2e] mb-4">Identity Documents</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Aadhar Number</label>
                  <p className="text-[#1a1a2e]">{myProfile.personal?.aadhar_number ? '****' + myProfile.personal.aadhar_number.slice(-4) : 'Not provided'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">PAN Number</label>
                  <p className="text-[#1a1a2e]">{myProfile.personal?.pan_number ? '****' + myProfile.personal.pan_number.slice(-4) : 'Not provided'}</p>
                </div>
              </div>
            </div>

            {/* Emergency Contact (Editable) */}
            <div className="glass-card p-6">
              <h3 className="font-bold text-lg text-[#1a1a2e] mb-4">Emergency Contact</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Contact Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.emergency_contact_name}
                      onChange={(e) => setEditForm({ ...editForm, emergency_contact_name: e.target.value })}
                      className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500"
                      placeholder="Emergency contact name"
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-neutral-400" />
                      <p className="text-[#1a1a2e]">{myProfile.emergency?.name || 'Not provided'}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Contact Number</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editForm.emergency_contact_number}
                      onChange={(e) => setEditForm({ ...editForm, emergency_contact_number: e.target.value })}
                      className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500"
                      placeholder="Emergency contact number"
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-neutral-400" />
                      <p className="text-[#1a1a2e]">{myProfile.emergency?.phone || 'Not provided'}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Departments Tab (View Only) */}
      {activeTab === 'departments' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {departments.length === 0 ? (
            <div className="col-span-full glass-card p-12 text-center text-neutral-500">
              No departments found.
            </div>
          ) : (
            departments.map(dept => {
              const deptEmployees = getDepartmentEmployees(dept);
              return (
                <div key={dept._id} className="soft-card overflow-hidden">
                  <div className="p-6 border-b border-white/30 bg-gradient-to-r from-yellow-50 to-yellow-100/50">
                    <h3 className="font-bold text-lg text-[#1a1a2e]">{dept.name}</h3>
                    <p className="text-sm text-neutral-500 mt-1">{dept.description || 'No description'}</p>
                  </div>
                  <div className="p-4">
                    <p className="text-sm font-medium text-neutral-700 mb-3">{deptEmployees.length} Members</p>
                    <div className="space-y-2">
                      {deptEmployees.slice(0, 5).map(emp => (
                        <div key={emp._id} className="flex items-center gap-3 text-sm">
                          <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                            <span className="text-yellow-600 font-medium text-xs">{(emp.personal?.full_name || emp.work_email)?.[0]?.toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="font-medium text-[#1a1a2e]">{emp.personal?.full_name || emp.work_email}</p>
                            <p className="text-xs text-neutral-500">{emp.designation}</p>
                          </div>
                        </div>
                      ))}
                      {deptEmployees.length > 5 && (
                        <p className="text-sm text-neutral-500">+{deptEmployees.length - 5} more</p>
                      )}
                      {deptEmployees.length === 0 && (
                        <p className="text-sm text-neutral-400">No members</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
